import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Layout } from "../components/Layout";
import { toast, Toaster } from "sonner";
import { MapPin, Briefcase, PaperPlaneTilt, Star, Users, X } from "@phosphor-icons/react";

const STATUSES = ["applied", "shortlisted", "selected", "rejected"];

export default function OpportunityDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const [op, setOp] = useState(null);
  const [applications, setApplications] = useState([]);
  const [matches, setMatches] = useState(null);
  const [message, setMessage] = useState("");
  const [proposed, setProposed] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/opportunities/${id}`);
    setOp(data);
    if (user && data.posted_by === user.id) {
      const { data: apps } = await api.get(`/opportunities/${id}/applications`);
      setApplications(apps);
      try {
        const { data: m } = await api.get(`/match/opportunity/${id}`);
        setMatches(m);
      } catch { /* ignore */ }
    }
  };
  useEffect(() => { load(); }, [id, user]);

  const apply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/applications", { opportunity_id: id, message, proposed_rate: proposed });
      toast.success("Applied!"); setMessage(""); setProposed("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("error_occurred")); }
    finally { setBusy(false); }
  };

  const updateStatus = async (appId, status) => {
    try {
      await api.put(`/applications/${appId}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("error_occurred")); }
  };

  const closeOp = async () => {
    try { await api.post(`/opportunities/${id}/close`); toast.success("Closed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || t("error_occurred")); }
  };

  if (!op) return <Layout><div className="p-10 text-center">Loading…</div></Layout>;
  const owner = user && op.posted_by === user.id;
  const canApply = user && (user.role === "skilled_individual" || user.role === "msme") && op.status === "open";

  return (
    <Layout>
      <Toaster richColors position="top-center" />
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <div className="as-card p-7">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <span className="as-badge-pill">{op.type}</span>
              <h1 className="text-3xl font-bold text-[var(--as-primary)] mt-3" style={{fontFamily:"Inter"}} data-testid="op-detail-title">{op.title}</h1>
              <div className="flex flex-wrap gap-3 mt-3 text-sm text-[var(--as-text-secondary)]">
                <span className="inline-flex items-center gap-1.5"><MapPin size={16} weight="duotone" />{op.city || (op.remote ? "Remote" : "—")}</span>
                <span className="inline-flex items-center gap-1.5"><Briefcase size={16} weight="duotone" />{op.posted_by_name}</span>
                {op.budget_min || op.budget_max ? (
                  <span className="inline-flex items-center gap-1.5"><Star size={16} weight="duotone" />{op.currency} {op.budget_min ?? "—"} – {op.budget_max ?? "—"}</span>
                ) : null}
              </div>
            </div>
            {owner && op.status === "open" && (
              <button onClick={closeOp} className="btn-secondary !py-2 !px-4" data-testid="op-close">
                <X size={16} weight="bold" /> {t("close_op")}
              </button>
            )}
            {op.status !== "open" && <span className="as-badge-skill">{t("op_closed")}</span>}
          </div>

          <p className="mt-5 text-[var(--as-text)] leading-relaxed whitespace-pre-wrap">{op.description}</p>
          <div className="flex flex-wrap gap-1.5 mt-5">
            {(op.required_skills || []).map((s) => <span key={s} className="as-badge-skill">{s}</span>)}
          </div>
        </div>

        {!owner && canApply && (
          <form onSubmit={apply} className="as-card p-7 space-y-4" data-testid="apply-form">
            <h2 className="text-xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{t("cta_apply")}</h2>
            <div>
              <label className="as-label">{t("apply_message")}</label>
              <textarea className="as-input min-h-[80px]" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="apply-message" />
            </div>
            {(op.type === "gig" || op.type === "rfq") && (
              <div>
                <label className="as-label">{t("proposed_rate")}</label>
                <input className="as-input" value={proposed} onChange={(e) => setProposed(e.target.value)} placeholder="₹2500/day" data-testid="apply-rate" />
              </div>
            )}
            <button type="submit" disabled={busy} className="btn-primary" data-testid="apply-submit">
              <PaperPlaneTilt size={18} weight="duotone" /> {busy ? "..." : t("cta_apply")}
            </button>
          </form>
        )}

        {!user && (
          <div className="as-card p-7 text-center">
            <p className="text-[var(--as-text-secondary)]">Login to apply</p>
            <Link to="/login" className="btn-primary mt-3 inline-flex">{t("login")}</Link>
          </div>
        )}

        {owner && (
          <div className="as-card p-7">
            <h2 className="text-xl font-bold text-[var(--as-primary)] mb-4 flex items-center gap-2" style={{fontFamily:"Inter"}}>
              <Users size={22} weight="duotone" /> {t("applicants")} ({applications.length})
            </h2>
            {applications.length === 0 ? <p className="text-[var(--as-text-secondary)]">{t("no_results")}</p> : (
              <ul className="divide-y divide-[var(--as-border)]">
                {applications.map((a) => (
                  <li key={a.id} className="py-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`applicant-${a.id}`}>
                    <div>
                      <Link to={`/profile/${a.applicant_id}`} className="font-bold text-[var(--as-primary)] hover:underline">{a.applicant_name}</Link>
                      <div className="text-xs text-[var(--as-text-secondary)]">{a.applicant_role} · {new Date(a.created_at).toLocaleDateString()}</div>
                      {a.message && <p className="text-sm mt-2">{a.message}</p>}
                      {a.proposed_rate && <p className="text-xs mt-1 font-bold">{t("proposed_rate")}: {a.proposed_rate}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.map((s) => (
                        <button key={s} onClick={() => updateStatus(a.id, s)} disabled={a.status === s}
                          className={`px-3 py-1 rounded-lg text-xs font-bold border ${a.status === s ? "bg-[var(--as-primary)] text-[var(--as-bg)] border-[var(--as-primary)]" : "bg-white text-[var(--as-primary)] border-[var(--as-border)] hover:bg-[var(--as-surface-muted)]"}`}
                          data-testid={`status-${s}-${a.id}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {owner && matches && matches.results.length > 0 && (
          <div className="as-card p-7">
            <h2 className="text-xl font-bold text-[var(--as-primary)] mb-4" style={{fontFamily:"Inter"}}>{t("matching_candidates")}</h2>
            <ul className="space-y-3">
              {matches.results.slice(0, 8).map((m) => (
                <li key={m.user.id} className="flex items-center justify-between py-2 border-b border-[var(--as-border)] last:border-0" data-testid={`match-${m.user.id}`}>
                  <Link to={`/profile/${m.user.id}`} className="font-bold text-[var(--as-primary)] hover:underline">{m.user.name}</Link>
                  <div className="flex items-center gap-3 text-sm text-[var(--as-text-secondary)]">
                    <span>{m.user.city || "—"}</span>
                    <span className="as-badge-pill">{t("match_score")}: {m.score}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
}
